import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close, Link as LinkIcon } from '@mui/icons-material';
import { InvestmentChecklistTaskDTO } from '@inversor-hub/types';
import DropzoneUploader from '@/components/form/DropzoneUploader';
import {
  useDeleteChecklistDocument,
  useUploadChecklistDocument,
} from '@/lib/api/investment-checklist';
import { useSnackbar } from 'notistack';

interface ChecklistDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  task: InvestmentChecklistTaskDTO;
  investmentId: string;
  canManageDocuments: boolean;
}

const ChecklistDocumentDialog = ({
  open,
  onClose,
  task,
  investmentId,
  canManageDocuments,
}: ChecklistDocumentDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: uploadDocument, isPending: isUploading } = useUploadChecklistDocument(
    investmentId,
    task.id
  );
  const { mutateAsync: deleteDocument, isPending: isDeleting } = useDeleteChecklistDocument(
    investmentId,
    task.id
  );
  const isBusy = isUploading || isDeleting;

  const handleClose = () => {
    if (!isBusy) {
      onClose();
    }
  };

  const handleDeleteDocument = async (mediaId: string) => {
    await deleteDocument(mediaId);
    enqueueSnackbar('Documento eliminado', { variant: 'success' });
  };

  const handleUploadComplete = () => {
    enqueueSnackbar(task.allowsMultipleDocuments ? 'Documentos subidos' : 'Documento subido', {
      variant: 'success',
    });

    if (!task.allowsMultipleDocuments) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isBusy}
      aria-labelledby={`checklist-documents-title-${task.id}`}
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle id={`checklist-documents-title-${task.id}`} sx={{ pr: 6 }}>
        <Typography variant="h6" component="span" fontWeight={700}>
          {task.documents.length > 0
            ? 'Gestionar documentos'
            : task.allowsMultipleDocuments
              ? 'Subir documentos'
              : 'Subir documento'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {task.label}
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={isBusy}
          size="small"
          aria-label="Cerrar"
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
        {canManageDocuments ? (
          <DropzoneUploader
            uploadType="documents"
            handleUploadFile={async (file) => {
              await uploadDocument(file);
            }}
            existingFiles={task.documents.map((document) => ({
              id: document.id,
              name: document.fileName,
              url: document.url,
              type: document.mimeType,
              canDelete: document.canDelete,
            }))}
            onDeleteExistingFile={handleDeleteDocument}
            maxFiles={task.allowsMultipleDocuments ? 10 : 1}
            disabled={isBusy}
            onUploadComplete={handleUploadComplete}
          />
        ) : task.documents.length > 0 ? (
          <Stack spacing={1}>
            <Typography variant="subtitle2">Documentos adjuntos</Typography>
            {task.documents.map((document) => (
              <Button
                key={document.id}
                href={document.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={<LinkIcon />}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  overflow: 'hidden',
                }}
              >
                <Box component="span" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  {document.fileName}
                </Box>
              </Button>
            ))}
          </Stack>
        ) : (
          <Alert severity="info">Esta tarea debe ser completada por el usuario asignado.</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isBusy}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChecklistDocumentDialog;
